import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { TOGGLE_SHOW_PANEL_LIVE_OPTION } from '../../../../../store/types';
import { USER_CLICK, USER_KEYBOARD, USER_REMOTE_CHAT_CLICKED, USER_REMOTE_HIGHLIGHTS_CLICKED, USER_REMOTE_INFOS_CLICKED, USER_REMOTE_PLAYLIST_CLICKED, USER_REMOTE_RECO_CLICKED, USER_REMOTE_ZAPPING_CLICKED } from '../../../../../types';
import { BUTTON_LAYER } from '../../../../common/constants';
import { usePlayerContext, useStyles } from '../../../../common/hooks';
import { isValidateKeyboard } from '../../../../common/utils/keyboard';
import { connect, withBreakPoints } from '../../../hoc';
import { togglePanel } from '../../../utils';
import { Button } from '../../common';
import { CHAT_NAME, EMPTY_ACTIVE_NAME, HIGHLIGHTS_NAME, INFOS_NAME, PLAYLIST_NAME, RECO_NAME, ZAPPING_NAME } from './constants';
import { ZAP_BUTTON_SIZE, ZAP_BUTTON_SIZE_OVERRIDE, ZAP_CONTENT, ZAP_ICON_SIZE, ZAP_WRAPPER } from './styles';

const handleClick = ({ player, setActive, isActive, name, action, source }) => {
  const activePanel = isActive ? EMPTY_ACTIVE_NAME : name;
  setActive(activePanel);
  togglePanel(player, { show: !isActive, source, name: activePanel });

  player.userEvents$.next({
    action,
    value: !isActive,
    source
  });
};

function Zap({ show, zappingEnabled, hasHighlight, hasChat, hasPlaylist, hasReco, hasInfos, activeCurrentTab }) {
  const setStyles = useStyles();
  const [currentTab, setActive] = useState(activeCurrentTab);
  const { size } = setStyles(ZAP_ICON_SIZE);

  /* Required for close button to reset state */
  useEffect(
    () => currentTab !== activeCurrentTab && setActive(activeCurrentTab),
    [activeCurrentTab, currentTab]
  );

  const isZappingActive = currentTab === ZAPPING_NAME;
  const isHighlightsActive = currentTab === HIGHLIGHTS_NAME;
  const isChatActive = currentTab === CHAT_NAME;
  const isPlaylistActive = currentTab === PLAYLIST_NAME;
  const isRecoActive = currentTab === RECO_NAME;
  const isInfoActive = currentTab === INFOS_NAME;

  const btns = [
    {
      name: ZAPPING_NAME,
      showBtn: zappingEnabled,
      label: 'Tous les directs',
      ariaLabel: 'Accéder à la liste des directs',
      isActive: isZappingActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_ZAPPING_CLICKED,
      icon: isZappingActive ? 'remoteControlActive' : 'remoteControl'
    },
    {
      name: HIGHLIGHTS_NAME,
      showBtn: hasHighlight,
      label: 'Les temps forts',
      ariaLabel: 'Accéder à la liste des moments forts',
      isActive: isHighlightsActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_HIGHLIGHTS_CLICKED,
      icon: !isHighlightsActive ? 'star' : 'starActive'
    },
    {
      name: CHAT_NAME,
      showBtn: hasChat,
      label: 'Live chat',
      ariaLabel: 'Accéder au live chat',
      isActive: isChatActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_CHAT_CLICKED,
      icon: !isChatActive ? 'chat' : 'chatActive'
    },
    {
      name: PLAYLIST_NAME,
      showBtn: hasPlaylist,
      label: 'Du même programme',
      ariaLabel: 'Accéder à la liste des vidéos du même programme',
      icon: isPlaylistActive ? 'playlist-active' : 'playlist',
      isActive: isPlaylistActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_PLAYLIST_CLICKED
    },
    {
      name: RECO_NAME,
      showBtn: hasReco,
      label: 'Recommandé pour vous',
      ariaLabel: 'Accéder à la liste des vidéos recommandées pour vous',
      icon: isRecoActive ? 'heart-active' : 'heart',
      isActive: isRecoActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_RECO_CLICKED
    },
    {
      name: INFOS_NAME,
      showBtn: hasInfos,
      label: 'Infos',
      ariaLabel: 'Accéder au résumé de la vidéo',
      icon: isInfoActive ? 'infos-active' : 'infos',
      isActive: isInfoActive,
      type: TOGGLE_SHOW_PANEL_LIVE_OPTION,
      action: USER_REMOTE_INFOS_CLICKED
    }
  ];

  const player = usePlayerContext();
  return show && (
    <div
      id="pannel-button-wrapper"
      style={ZAP_WRAPPER}
      onClick={(e) => e.stopPropagation()}
      role="presentation"
    >
      <div style={setStyles(ZAP_CONTENT)}>
        {
          btns.map(({ name, showBtn, label, ariaLabel, isActive, type, action, icon }) => (showBtn && (
            <Button
              styleOverride={ZAP_BUTTON_SIZE_OVERRIDE}
              style={setStyles(ZAP_BUTTON_SIZE)}
              styles={{
                subWrapperStyle: {
                  height: 'inherit'
                }
              }}
              label={label}
              ariaLabel={ariaLabel}
              ariaHidden="false"
              onClick={(e) => e instanceof PointerEvent && handleClick({
                player,
                setActive,
                isActive,
                type,
                name,
                action,
                source: USER_CLICK
              })}
              xs
              onKeyDown={(e) => isValidateKeyboard(e) && handleClick({
                player,
                setActive,
                isActive,
                type,
                name,
                action,
                source: USER_KEYBOARD
              })}
              name={name}
              disableFocus={false}
              zIndex={BUTTON_LAYER + 1}
              type="icon"
              icon={icon}
              size={size}
              zone="left"
            />
          )
          ))
        }
      </div>
    </div>
  );
}

const selector = ({
  zapping: { list, zappingEnabled = list.length > 0 },
  ui: {
    chatAvailable, waitStart, isExtraSmallScreen, isSmallScreen, isFullscreen, panelLiveOption: { currentTab: activeCurrentTab },
    hasChat = chatAvailable && isFullscreen && !isSmallScreen && !isExtraSmallScreen
  },
  playlist: { playlist, hasPlaylist = playlist.length > 0 },
  highlights: { pinsPosition, hasHighlight = !!pinsPosition.length },
  recommendations: {
    recommendations,
    hasReco = recommendations.length > 0 && isFullscreen
  },
  media: { isAd, isDAI, isLive, description, hasInfos = !!description && isFullscreen },
  ad: { adType }
}) => ({
  show: (
    (!isAd || (isAd && adType !== 'PREROLL' && isDAI))
    && !isExtraSmallScreen
    && !waitStart
    && (
      (isLive && (zappingEnabled || hasHighlight || hasChat))
      || (!isLive && (hasPlaylist || hasReco || hasInfos))
    )
  ),
  zappingEnabled: isLive && zappingEnabled,
  hasHighlight: isLive && hasHighlight,
  hasChat: isLive && hasChat,
  hasPlaylist: !isLive && hasPlaylist,
  hasReco: !isLive && hasReco,
  hasInfos: !isLive && hasInfos,
  activeCurrentTab
});
export default withBreakPoints(connect(selector)(Zap));
